'use client';

import Button from '@/components/ui/Button';
import Link from 'next/link';
import { signInWithPassword } from '@/utils/auth-helpers/server';
import { useRouter } from 'next/navigation';
import React, { useState } from 'react';
import { Dictionary, Locales } from "@/app/[lang]/dictonaries";

// Define prop type with allowEmail boolean
interface PasswordSignInProps {
    allowEmail: boolean;
    tempSelectionId: string;
    checkoutType: string;
    dict: Dictionary,
    lang: Locales
    giftcard?: string;
}

export default function PasswordSignIn({ allowEmail, tempSelectionId, checkoutType, dict, lang, giftcard }: PasswordSignInProps) {
    const router = useRouter();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        setIsSubmitting(true); // Disable the button while the request is being handled

        const formData = new FormData(e.currentTarget);
        const redirectPath = await signInWithPassword(formData, tempSelectionId, checkoutType, giftcard);

        // Always use router.push for client-side navigation
        router.push(`/${lang}/` + redirectPath);

        setIsSubmitting(false);
    };

    return (
        <div className="my-8">
            <form
                noValidate={true}
                className="mb-4"
                onSubmit={(e) => handleSubmit(e)}
            >
                <div className="grid gap-2">
                    <div className="grid gap-1">
                        <label className={"text-sm"} htmlFor="email">{dict.pages.sign_in.forms.pw_sign_in.email}</label>
                        <input
                            id="email"
                            placeholder={dict.pages.sign_in.forms.pw_sign_in.email_placeholder}
                            type="email"
                            name="email"
                            autoCapitalize="none"
                            autoComplete="email"
                            autoCorrect="off"
                            className="w-full p-3 rounded-md bg-gray-200 outline-gray-400"
                        />
                        <label className={"text-sm mt-4"} htmlFor="password">{dict.pages.sign_in.forms.pw_sign_in.password}</label>
                        <input
                            id="password"
                            placeholder={dict.pages.sign_in.forms.pw_sign_in.password}
                            type="password"
                            name="password"
                            autoComplete="current-password"
                            className="w-full p-3 rounded-md bg-zinc-200 outline-gray-400"
                        />
                        <div className={"flex flex-row justify-end items-center"}>
                            <Link href={`/${lang}/manage-password`}>
                                <p className="mt-0.5 text-xs">{dict.pages.sign_in.forms.pw_sign_in.forgot_pw}</p>
                            </Link>
                        </div>
                    </div>
                    <Button
                        variant="slim"
                        type="submit"
                        className="mt-8"
                        loading={isSubmitting}
                    >
                        {dict.pages.sign_in.forms.pw_sign_in.sign_in}
                    </Button>
                </div>
            </form>
            <Link href={tempSelectionId ? `/${lang}/signup/?type=meal-plan&tempSelectionId=${tempSelectionId}${giftcard ? `&giftcard=${giftcard}` : ''}` : `/${lang}/signup`} className={"mt-4 flex flex-row gap-1 "}>
                <p className={"text-sm text-zinc-800"}>
                    Don't have an account?
                </p>
                <p className={"text-sm flex flex-row text-[#213735] font-bold underline-offset-4 hover:underline"}>{dict.pages.sign_in.forms.pw_sign_in.sign_up}</p>
            </Link>
        </div>
    );
}

